import { NS, type MissionCard, type NamespaceTuple } from '@ownyou/shared-types';
import { createMissionSpineRunId, recordMissionSpineMarker } from '../mission-spine/instrumentation';

export type MissionState =
  | 'suggested'
  | 'viewed'
  | 'accepted'
  | 'dismissed'
  | 'snoozed'
  | 'in_progress'
  | 'completed'
  | 'expired';

export interface StateTransition {
  from: MissionState;
  to: MissionState;
  at: number;
  reason?: string;
}

export const MISSION_LIFECYCLE_NS = ['missions', 'lifecycle'] as const satisfies NamespaceTuple;

export type LegacyMissionStatus = 'ACTIVE' | 'SNOOZED' | 'DISMISSED' | 'COMPLETED' | 'CREATED' | 'PRESENTED';

export interface CardFirstMissionCard extends MissionCard {
  state?: MissionState;
  status: LegacyMissionStatus;
  acceptedAt?: string;
  completedAt?: string;
  dismissedAt?: string;
  snoozedUntil?: number;
}

export interface MissionLifecycleHistory {
  missionId: string;
  state: MissionState;
  transitions: StateTransition[];
  createdAt: number;
  acceptedAt?: number;
  completedAt?: number;
  dismissedAt?: number;
  snoozedUntil?: number;
  snoozeCount: number;
}

export interface MissionLifecycleStore {
  get<T>(namespace: NamespaceTuple, key: string): Promise<T | null>;
  put<T>(namespace: NamespaceTuple, key: string, value: T): Promise<void>;
}

export interface TransitionMissionCardLifecycleInput {
  store: MissionLifecycleStore;
  userId: string;
  missionId: string;
  to: MissionState;
  reason?: string;
  snoozedUntil?: number;
  now?: () => number;
}

export interface CompleteMissionCardLifecycleInput {
  store: MissionLifecycleStore;
  userId: string;
  missionId: string;
  reason?: string;
  now?: () => number;
}

export interface TransitionMissionCardLifecycleResult {
  state: MissionState;
  card: CardFirstMissionCard;
  lifecycle: MissionLifecycleHistory;
}

export class MissionLifecycleTransitionError extends Error {
  constructor(
    public readonly from: MissionState,
    public readonly to: MissionState,
  ) {
    super(`Invalid mission lifecycle transition: ${from} -> ${to}`);
    this.name = 'MissionLifecycleTransitionError';
  }
}

const VALID_TRANSITIONS: Record<MissionState, readonly MissionState[]> = {
  suggested: ['viewed', 'accepted', 'snoozed', 'dismissed', 'expired'],
  viewed: ['accepted', 'snoozed', 'dismissed', 'expired'],
  accepted: ['in_progress', 'completed', 'snoozed', 'dismissed', 'expired'],
  in_progress: ['completed', 'snoozed', 'dismissed', 'expired'],
  snoozed: ['suggested', 'dismissed', 'expired'],
  dismissed: [],
  completed: [],
  expired: [],
};

function legacyStatusForState(state: MissionState): LegacyMissionStatus {
  switch (state) {
    case 'snoozed':
      return 'SNOOZED';
    case 'dismissed':
    case 'expired':
      return 'DISMISSED';
    case 'completed':
      return 'COMPLETED';
    default:
      return 'ACTIVE';
  }
}

export function getMissionCardLifecycleState(
  card: Pick<CardFirstMissionCard, 'state' | 'status' | 'snoozedUntil'>,
  now = Date.now(),
): MissionState {
  if (card.state) {
    if (card.state === 'snoozed' && card.snoozedUntil != null && card.snoozedUntil <= now) {
      return 'suggested';
    }
    return card.state;
  }

  switch (card.status) {
    case 'SNOOZED':
      return card.snoozedUntil != null && card.snoozedUntil <= now ? 'suggested' : 'snoozed';
    case 'DISMISSED':
      return 'dismissed';
    case 'COMPLETED':
      return 'completed';
    default:
      return 'suggested';
  }
}

function assertValidTransition(from: MissionState, to: MissionState): void {
  if (!VALID_TRANSITIONS[from]?.includes(to)) {
    throw new MissionLifecycleTransitionError(from, to);
  }
}

function snoozeDurationMs(reason?: string): number {
  return reason === '1_week' ? 7 * 24 * 60 * 60 * 1000 : 24 * 60 * 60 * 1000;
}

function resolveSnoozedUntil(timestamp: number, reason?: string, explicitSnoozedUntil?: number): number {
  return explicitSnoozedUntil ?? timestamp + snoozeDurationMs(reason);
}

function timestampIso(timestamp: number): string {
  return new Date(timestamp).toISOString();
}

function updatedCardForTransition(
  card: CardFirstMissionCard,
  to: MissionState,
  timestamp: number,
  reason?: string,
  explicitSnoozedUntil?: number,
): CardFirstMissionCard {
  const next: CardFirstMissionCard = {
    ...card,
    state: to,
    status: legacyStatusForState(to),
  };

  if (to !== 'snoozed') {
    delete next.snoozedUntil;
  }

  switch (to) {
    case 'accepted':
      next.acceptedAt = timestampIso(timestamp);
      break;
    case 'completed':
      next.completedAt = timestampIso(timestamp);
      break;
    case 'dismissed':
    case 'expired':
      next.dismissedAt = timestampIso(timestamp);
      break;
    case 'snoozed':
      next.snoozedUntil = resolveSnoozedUntil(timestamp, reason, explicitSnoozedUntil);
      break;
  }

  return next;
}

function defaultLifecycle(missionId: string, state: MissionState, createdAt: number): MissionLifecycleHistory {
  return {
    missionId,
    state,
    transitions: [],
    createdAt,
    snoozeCount: 0,
  };
}

function updatedLifecycleForTransition(
  existing: MissionLifecycleHistory | null,
  missionId: string,
  from: MissionState,
  to: MissionState,
  timestamp: number,
  reason?: string,
  explicitSnoozedUntil?: number,
): MissionLifecycleHistory {
  const lifecycle: MissionLifecycleHistory = existing
    ? { ...existing, transitions: [...existing.transitions] }
    : defaultLifecycle(missionId, from, timestamp);

  lifecycle.state = to;
  lifecycle.transitions.push({ from, to, at: timestamp, reason });

  switch (to) {
    case 'accepted':
      lifecycle.acceptedAt = timestamp;
      break;
    case 'completed':
      lifecycle.completedAt = timestamp;
      break;
    case 'dismissed':
    case 'expired':
      lifecycle.dismissedAt = timestamp;
      break;
    case 'snoozed':
      lifecycle.snoozedUntil = resolveSnoozedUntil(timestamp, reason, explicitSnoozedUntil);
      lifecycle.snoozeCount += 1;
      break;
  }

  if (to !== 'snoozed') {
    delete lifecycle.snoozedUntil;
  }

  return lifecycle;
}

function completionPathFrom(from: MissionState): readonly MissionState[] {
  switch (from) {
    case 'completed':
      return [];
    case 'accepted':
    case 'in_progress':
      return ['completed'];
    case 'suggested':
    case 'viewed':
      return ['accepted', 'completed'];
    case 'snoozed':
      return ['suggested', 'accepted', 'completed'];
    case 'dismissed':
    case 'expired':
      throw new MissionLifecycleTransitionError(from, 'completed');
  }
}

function updatedCardForTransitionPath(
  card: CardFirstMissionCard,
  path: readonly MissionState[],
  timestamp: number,
  reason?: string,
): CardFirstMissionCard {
  let next = card;

  for (const to of path) {
    next = updatedCardForTransition(
      next,
      to,
      timestamp,
      to === 'completed' ? reason : undefined,
    );
  }

  return next;
}

function updatedLifecycleForTransitionPath(
  existing: MissionLifecycleHistory | null,
  missionId: string,
  from: MissionState,
  path: readonly MissionState[],
  timestamp: number,
  reason?: string,
): MissionLifecycleHistory {
  let lifecycle = existing
    ? { ...existing, transitions: [...existing.transitions] }
    : defaultLifecycle(missionId, from, timestamp);
  let current = from;

  for (const to of path) {
    assertValidTransition(current, to);
    lifecycle = updatedLifecycleForTransition(
      lifecycle,
      missionId,
      current,
      to,
      timestamp,
      to === 'completed' ? reason : undefined,
    );
    current = to;
  }

  return lifecycle;
}

export async function transitionMissionCardLifecycle(
  input: TransitionMissionCardLifecycleInput,
): Promise<TransitionMissionCardLifecycleResult> {
  const timestamp = input.now?.() ?? Date.now();
  const missionNamespace = NS.missionCards(input.userId);
  const card = await input.store.get<CardFirstMissionCard>(missionNamespace, input.missionId);

  if (!card) {
    throw new Error(`Mission ${input.missionId} not found`);
  }

  const from = getMissionCardLifecycleState(card, timestamp);
  assertValidTransition(from, input.to);

  const updatedCard = updatedCardForTransition(
    card,
    input.to,
    timestamp,
    input.reason,
    input.snoozedUntil,
  );
  const existingLifecycle = await input.store.get<MissionLifecycleHistory>(
    MISSION_LIFECYCLE_NS,
    input.missionId,
  );
  const lifecycle = updatedLifecycleForTransition(
    existingLifecycle,
    input.missionId,
    from,
    input.to,
    timestamp,
    input.reason,
    input.snoozedUntil,
  );

  await input.store.put(missionNamespace, input.missionId, updatedCard);
  await input.store.put(MISSION_LIFECYCLE_NS, input.missionId, lifecycle);

  recordMissionSpineMarker({
    runId: createMissionSpineRunId('slice-02-lifecycle'),
    hop: 'lifecycle_transition',
    status: 'success',
    namespace: missionNamespace[0],
    reasonCode: `${from}_to_${input.to}`,
    counts: {
      cardWriteCount: 1,
      lifecycleWriteCount: 1,
      transitionCount: lifecycle.transitions.length,
    },
  }, { log: false });

  return {
    state: input.to,
    card: updatedCard,
    lifecycle,
  };
}

export async function completeMissionCardLifecycle(
  input: CompleteMissionCardLifecycleInput,
): Promise<TransitionMissionCardLifecycleResult> {
  const timestamp = input.now?.() ?? Date.now();
  const missionNamespace = NS.missionCards(input.userId);
  const card = await input.store.get<CardFirstMissionCard>(missionNamespace, input.missionId);

  if (!card) {
    throw new Error(`Mission ${input.missionId} not found`);
  }

  const from = getMissionCardLifecycleState(card, timestamp);
  const existingLifecycle = await input.store.get<MissionLifecycleHistory>(
    MISSION_LIFECYCLE_NS,
    input.missionId,
  );
  const path = completionPathFrom(from);

  if (path.length === 0) {
    return {
      state: 'completed',
      card,
      lifecycle: existingLifecycle ?? defaultLifecycle(input.missionId, 'completed', timestamp),
    };
  }

  const updatedCard = updatedCardForTransitionPath(card, path, timestamp, input.reason);
  const lifecycle = updatedLifecycleForTransitionPath(
    existingLifecycle,
    input.missionId,
    from,
    path,
    timestamp,
    input.reason,
  );

  await input.store.put(missionNamespace, input.missionId, updatedCard);
  await input.store.put(MISSION_LIFECYCLE_NS, input.missionId, lifecycle);

  recordMissionSpineMarker({
    runId: createMissionSpineRunId('slice-02-lifecycle'),
    hop: 'lifecycle_transition',
    status: 'success',
    namespace: missionNamespace[0],
    reasonCode: `${from}_to_${path.join('_to_')}`,
    counts: {
      cardWriteCount: 1,
      lifecycleWriteCount: 1,
      pathTransitionCount: path.length,
      transitionCount: lifecycle.transitions.length,
    },
  }, { log: false });

  return {
    state: 'completed',
    card: updatedCard,
    lifecycle,
  };
}
